import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tabs,
  Tab,
  Badge,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Fab
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Message as MessageIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  LocalShipping as ShippingIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  ShoppingCart as CartIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components
const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 600,
  ...(status === 'pending' && {
    backgroundColor: '#fff3cd',
    color: '#856404',
    border: '1px solid #ffeaa7'
  }),
  ...(status === 'processing' && {
    backgroundColor: '#cce5ff',
    color: '#004085',
    border: '1px solid #b3d9ff'
  }),
  ...(status === 'shipped' && {
    backgroundColor: '#d4edda',
    color: '#155724',
    border: '1px solid #c3e6cb'
  }),
  ...(status === 'delivered' && {
    backgroundColor: '#d1ecf1',
    color: '#0c5460',
    border: '1px solid #bee5eb'
  }),
  ...(status === 'cancelled' && {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb'
  }),
  ...(status === 'returned' && {
    backgroundColor: '#e2e3e5',
    color: '#383d41',
    border: '1px solid #d6d8db'
  })
}));

const PriorityChip = styled(Chip)(({ theme, priority }) => ({
  fontWeight: 600,
  ...(priority === 'high' && {
    backgroundColor: '#f8d7da',
    color: '#721c24'
  }),
  ...(priority === 'medium' && {
    backgroundColor: '#fff3cd',
    color: '#856404'
  }),
  ...(priority === 'low' && {
    backgroundColor: '#d1ecf1',
    color: '#0c5460'
  })
}));

const OrderSupport = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailDialog, setOrderDetailDialog] = useState(false);
  const [supportDialog, setSupportDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  // Mock data for orders
  const [orders, setOrders] = useState([
    {
      id: 'ORD-001',
      customerName: 'রহিম উদ্দিন',
      customerPhone: '01712345678',
      customerEmail: 'rahim@email.com',
      orderDate: '2024-01-15',
      deliveryDate: '2024-01-18',
      status: 'pending',
      priority: 'high',
      totalAmount: 1250,
      items: 3,
      store: 'মদিনা স্টোর',
      location: 'ঢাকা',
      issue: 'ডেলিভারি সমস্যা',
      description: 'অর্ডারটি ৩ দিন ধরে পেন্ডিং আছে'
    },
    {
      id: 'ORD-002',
      customerName: 'ফাতেমা খাতুন',
      customerPhone: '01823456789',
      customerEmail: 'fatema@email.com',
      orderDate: '2024-01-14',
      deliveryDate: '2024-01-17',
      status: 'processing',
      priority: 'medium',
      totalAmount: 890,
      items: 2,
      store: 'আল-আমিন মার্কেট',
      location: 'চট্টগ্রাম',
      issue: 'পেমেন্ট সমস্যা',
      description: 'পেমেন্ট কনফার্ম হলেও অর্ডার প্রসেস হচ্ছে না'
    },
    {
      id: 'ORD-003',
      customerName: 'করিম মিয়া',
      customerPhone: '01934567890',
      customerEmail: 'karim@email.com',
      orderDate: '2024-01-13',
      deliveryDate: '2024-01-16',
      status: 'shipped',
      priority: 'low',
      totalAmount: 2100,
      items: 5,
      store: 'সুন্দরবন স্টোর',
      location: 'খুলনা',
      issue: 'ট্র্যাকিং সমস্যা',
      description: 'অর্ডার শিপ হয়েছে কিন্তু ট্র্যাকিং নম্বর পাওয়া যাচ্ছে না'
    },
    {
      id: 'ORD-004',
      customerName: 'সালমা বেগম',
      customerPhone: '01645678901',
      customerEmail: 'salma@email.com',
      orderDate: '2024-01-12',
      deliveryDate: '2024-01-15',
      status: 'delivered',
      priority: 'low',
      totalAmount: 750,
      items: 1,
      store: 'রাজশাহী মার্কেট',
      location: 'রাজশাহী',
      issue: 'রিটার্ন রিকোয়েস্ট',
      description: 'পণ্য ডেলিভারি হয়েছে কিন্তু রিটার্ন করতে চায়'
    },
    {
      id: 'ORD-005',
      customerName: 'আব্দুল্লাহ আল মামুন',
      customerPhone: '01556789012',
      customerEmail: 'mamun@email.com',
      orderDate: '2024-01-11',
      deliveryDate: '2024-01-14',
      status: 'cancelled',
      priority: 'high',
      totalAmount: 1800,
      items: 4,
      store: 'সিলেট স্টোর',
      location: 'সিলেট',
      issue: 'অর্ডার ক্যানসেল',
      description: 'অর্ডার ক্যানসেল করতে চায় কিন্তু রিফান্ড পায়নি'
    }
  ]);

  const [filteredOrders, setFilteredOrders] = useState(orders);

  // Filter orders based on search and filters
  useEffect(() => {
    let filtered = orders;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(order =>
        order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.customerPhone.includes(searchQuery) ||
        order.store.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(order => order.priority === priorityFilter);
    }

    setFilteredOrders(filtered);
  }, [searchQuery, statusFilter, priorityFilter, orders]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setOrderDetailDialog(true);
  };

  const handleSupportClick = (order) => {
    setSelectedOrder(order);
    setSupportDialog(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <PendingIcon />;
      case 'processing': return <AssignmentIcon />;
      case 'shipped': return <ShippingIcon />;
      case 'delivered': return <CheckCircleIcon />;
      case 'cancelled': return <CancelIcon />;
      case 'returned': return <WarningIcon />;
      default: return <PendingIcon />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'পেন্ডিং';
      case 'processing': return 'প্রসেসিং';
      case 'shipped': return 'শিপড';
      case 'delivered': return 'ডেলিভারড';
      case 'cancelled': return 'ক্যানসেলড';
      case 'returned': return 'রিটার্নড';
      default: return 'অজানা';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 'high': return 'উচ্চ';
      case 'medium': return 'মাঝারি';
      case 'low': return 'নিম্ন';
      default: return 'অজানা';
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const handleCallCustomer = (phone) => {
    // Implement call functionality
    console.log('Calling customer:', phone);
  };

  const handleEmailCustomer = (email) => {
    // Implement email functionality
    window.open(`mailto:${email}`, '_blank');
  };

  const handleMessageCustomer = (phone) => {
    // Implement messaging functionality
    console.log('Messaging customer:', phone);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          অর্ডার সাপোর্ট
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : 'রিফ্রেশ'}
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AssignmentIcon sx={{ fontSize: 40, color: '#1976d2', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {orders.filter(o => o.status === 'pending').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    পেন্ডিং অর্ডার
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ShippingIcon sx={{ fontSize: 40, color: '#2e7d32', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {orders.filter(o => o.status === 'shipped').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    শিপড অর্ডার
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon sx={{ fontSize: 40, color: '#ed6c02', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {orders.filter(o => o.status === 'delivered').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ডেলিভারড অর্ডার
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WarningIcon sx={{ fontSize: 40, color: '#d32f2f', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {orders.filter(o => o.priority === 'high').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    উচ্চ প্রাধান্য
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="সকল অর্ডার" />
          <Tab label="পেন্ডিং" />
          <Tab label="প্রসেসিং" />
          <Tab label="শিপড" />
          <Tab label="সমস্যা" />
        </Tabs>
      </Paper>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="অর্ডার আইডি, গ্রাহকের নাম, ফোন নম্বর বা স্টোর খুঁজুন..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>স্ট্যাটাস</InputLabel>
              <Select
                value={statusFilter}
                label="স্ট্যাটাস"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">সকল</MenuItem>
                <MenuItem value="pending">পেন্ডিং</MenuItem>
                <MenuItem value="processing">প্রসেসিং</MenuItem>
                <MenuItem value="shipped">শিপড</MenuItem>
                <MenuItem value="delivered">ডেলিভারড</MenuItem>
                <MenuItem value="cancelled">ক্যানসেলড</MenuItem>
                <MenuItem value="returned">রিটার্নড</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>প্রাধান্য</InputLabel>
              <Select
                value={priorityFilter}
                label="প্রাধান্য"
                onChange={(e) => setPriorityFilter(e.target.value)}
              >
                <MenuItem value="all">সকল</MenuItem>
                <MenuItem value="high">উচ্চ</MenuItem>
                <MenuItem value="medium">মাঝারি</MenuItem>
                <MenuItem value="low">নিম্ন</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
                setPriorityFilter('all');
              }}
            >
              রিসেট
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Orders Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>অর্ডার আইডি</TableCell>
                <TableCell>গ্রাহক</TableCell>
                <TableCell>স্টোর</TableCell>
                <TableCell>তারিখ</TableCell>
                <TableCell>পরিমাণ</TableCell>
                <TableCell>স্ট্যাটাস</TableCell>
                <TableCell>প্রাধান্য</TableCell>
                <TableCell>সমস্যা</TableCell>
                <TableCell>অ্যাকশন</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {order.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {order.customerName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {order.customerPhone}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">
                        {order.store}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {order.location}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">
                        {new Date(order.orderDate).toLocaleDateString('bn-BD')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(order.deliveryDate).toLocaleDateString('bn-BD')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        ৳{order.totalAmount}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {order.items} আইটেম
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      icon={getStatusIcon(order.status)}
                      label={getStatusText(order.status)}
                      status={order.status}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <PriorityChip
                      label={getPriorityText(order.priority)}
                      priority={order.priority}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="error">
                      {order.issue}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="অর্ডার দেখুন">
                        <IconButton
                          size="small"
                          onClick={() => handleOrderClick(order)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="সাপোর্ট">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleSupportClick(order)}
                        >
                          <MessageIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="কল করুন">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleCallCustomer(order.customerPhone)}
                        >
                          <PhoneIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Order Detail Dialog */}
      <Dialog
        open={orderDetailDialog}
        onClose={() => setOrderDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          অর্ডার বিবরণ - {selectedOrder?.id}
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>গ্রাহকের তথ্য</Typography>
                <List>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        <PeopleIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={selectedOrder.customerName}
                      secondary={`ফোন: ${selectedOrder.customerPhone}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="ইমেইল"
                      secondary={selectedOrder.customerEmail}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>অর্ডার তথ্য</Typography>
                <List>
                  <ListItem>
                    <ListItemAvatar>
                      <StoreIcon />
                    </ListItemAvatar>
                    <ListItemText
                      primary={selectedOrder.store}
                      secondary={selectedOrder.location}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemAvatar>
                      <MoneyIcon />
                    </ListItemAvatar>
                    <ListItemText
                      primary={`৳${selectedOrder.totalAmount}`}
                      secondary={`${selectedOrder.items} আইটেম`}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>সমস্যা</Typography>
                <Alert severity="warning">
                  <Typography variant="subtitle2">{selectedOrder.issue}</Typography>
                  <Typography variant="body2">{selectedOrder.description}</Typography>
                </Alert>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDetailDialog(false)}>বন্ধ করুন</Button>
          <Button
            variant="contained"
            onClick={() => {
              setOrderDetailDialog(false);
              setSupportDialog(true);
            }}
          >
            সাপোর্ট শুরু করুন
          </Button>
        </DialogActions>
      </Dialog>

      {/* Support Dialog */}
      <Dialog
        open={supportDialog}
        onClose={() => setSupportDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          সাপোর্ট - {selectedOrder?.id}
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>গ্রাহকের সাথে যোগাযোগ</Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<PhoneIcon />}
                    onClick={() => handleCallCustomer(selectedOrder.customerPhone)}
                  >
                    কল করুন
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<EmailIcon />}
                    onClick={() => handleEmailCustomer(selectedOrder.customerEmail)}
                  >
                    ইমেইল
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<MessageIcon />}
                    onClick={() => handleMessageCustomer(selectedOrder.customerPhone)}
                  >
                    মেসেজ
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>সমস্যা সমাধান</Typography>
                <Alert severity="info">
                  <Typography variant="body2">
                    গ্রাহকের সমস্যা সমাধানের জন্য উপরের যোগাযোগের বিকল্পগুলি ব্যবহার করুন।
                    সমস্যা সমাধানের পর অর্ডার স্ট্যাটাস আপডেট করুন।
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupportDialog(false)}>বন্ধ করুন</Button>
          <Button variant="contained" color="success">
            সমস্যা সমাধান হয়েছে
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="নতুন সাপোর্ট"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default OrderSupport; 